import { useMutation } from '@apollo/client';
import { useContext, useMemo, useEffect, useState, useRef } from 'react';
import { TeacherEvaluationPageContext, activeSubmissionSelector } from '../../context';
import Select, { Option, SelectProps } from 'rc-select';
import { toast } from 'react-toastify';
import cx from 'classnames';
import { sum, isEqual, isEmpty } from 'lodash';
import {
  GetTeacherEvaluationPageQuery,
  SubmissionCreditsInput,
  SubmissionRubricCredits,
  SubmissionStatus,
  SubmitAssignmentCreditsDocument,
  UpdateSubmissionStatusDocument,
} from '../../../../services/graphql/types/graphql';
import { useStoreContext } from '../../../../factorys/useStoreContext';
import { IdentityContext } from '../../../App/identity.context';
import { Transcript } from '../../../Transcript';
import { CaretDownOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { getFragmentData } from '../../../../services/graphql/types';
import { TeacherEvaluationPageSubmissionFragmentDoc, TeacherEvaluationPageSubmissionFragment } from '../../../../services/graphql/types/graphql';
import { useAiEvaluation } from './useAiEvaluation';
import './styles.scss';

type AiEvaluationResult = {
  promptId: 'guided' | 'creative' | 'teacher';
  promptName: string;
  provider: 'qwen' | 'doubao' | 'glm';
  status: 'success' | 'error' | 'not_configured';
  model?: string;
  evaluation?: string;
  error?: string;
  durationMs: number;
};

const aiProviderNames: Record<AiEvaluationResult['provider'], string> = {
  qwen: '阿里 Qwen',
  doubao: '火山豆包',
  glm: '智谱 GLM',
};

export const GradeNumberSelect = ({
  value,
  readOnly = false,
  onChange,
}: {
  value: number;
  readOnly?: boolean;
  onChange: (value: number) => void;
}) => {
  return (
    <div className="flex space-x-3">
      {[0, 1, 2, 3, 4, 5].map((i) => {
        return (
          <div
            className={cx(
              'expand-clickable-area-5px',
              readOnly ? 'cursor-not-allowed' : 'cursor-pointer',
              { 'text-green-500': i === value },
            )}
            key={`extra-comment-credit-${i}`}
            onClick={() => (!readOnly ? onChange(i) : () => void 0)}
          >
            {i}
          </div>
        );
      })}
    </div>
  );
};

export const RubricInput: React.FC<{
  handleRubricLevelChange?: any;
  handleRubricCommentChange?: any;
  isReadOnly: boolean;
  assignmentRubric: GetTeacherEvaluationPageQuery['assignment']['assignmentRubrics'][0];
  rubricLevelValue: string;
  rubricComment: string;
}> = ({
  handleRubricCommentChange,
  assignmentRubric,
  isReadOnly,
  handleRubricLevelChange,
  rubricLevelValue,
  rubricComment,
}) => {

  return (
    <div key={`${JSON.stringify(assignmentRubric)}`}>
      <div className="text-[16px] leading-[50px] flex items-center justify-between w-full">
        <div className="flex-shrink-0 mr-4">{assignmentRubric.rubric.name}</div>
        <Select
          suffixIcon={() => <CaretDownOutlined />}
          disabled={isReadOnly}
          className="rubric-selection-box"
          onChange={handleRubricLevelChange(assignmentRubric.rubric.rubricId)}
          value={rubricLevelValue}
        >
          {assignmentRubric.rubric.rubricLevels
            .toSorted((a, b) => b.grade - a.grade)
            .map((rl) => {
              return (
                <Option key={rl.rubricLevelId}>
                  {rl.text} ({rl.grade}%)
                </Option>
              );
            })}
        </Select>
      </div>
      <div className="bg-transparent text-white text-[14px] leading-[20px] w-[100%] py-[10px] border-x-0 border-y border-white border-dashed carret-white">
        <div className="w-full flex">
          <label className="flex-shrink-0">额外评价：</label>
          <textarea
            className="caret-white bg-inherit text-white w-full"
            disabled={isReadOnly}
            value={rubricComment}
            onChange={handleRubricCommentChange(assignmentRubric.rubric.rubricId)}
          ></textarea>
        </div>
      </div>
    </div>
  );
};

// Constants
const ON_TIME_SUBMISSION_RUBRIC_ID = '038c35d4-aae8-4d39-8524-ea9be92a3de4';

// Helper function to check if submission is on time
export const isSubmissionOnTime = (dueDate: string, submitDate: string): boolean => {
  const due = new Date(dueDate);
  const submit = new Date(/^\d+$/.test(submitDate) ? Number(submitDate) : submitDate);
  return !isNaN(submit.getTime()) && submit <= due;
};

export const CommentEditor = ({ artworkId }: { artworkId: string }): JSX.Element => {
  const { state } = useContext(TeacherEvaluationPageContext);

  const { currentIdentity, currentClass } = useStoreContext(IdentityContext);
  const role = currentIdentity?.identity;
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);

  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showRetractConfirm, setShowRetractConfirm] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [showAiEvaluation, setShowAiEvaluation] = useState(false);

  const serverSubmission = useMemo(
    () => activeSubmissionSelector(state, currentIdentity!.identity),
    [currentIdentity, state],
  );
  // The editor is keyed by submission and role. Unrelated query updates must not erase drafts.
  const [activeSubmission, setActiveSubmission] = useState<TeacherEvaluationPageSubmissionFragment>(serverSubmission);
  const [actionBusy, setActionBusy] = useState(false);
  const actionLock = useRef(false);
  const { job: aiJob, loading: aiFetching, error: aiError, enqueue: enqueueAi } = useAiEvaluation(activeSubmission.submissionId, role === 'teacher');
  const aiLoading = aiFetching || aiJob?.status === 'queued' || aiJob?.status === 'running';
  const aiResults = aiJob?.results ?? [];
  const isReadOnly = useMemo(() => {
    if (role === 'teacher') {
      return [SubmissionStatus.Completed, SubmissionStatus.CompletedButNoGrade].includes(
        activeSubmission.submissionStatus,
      );
    }
    if (role === 'ta') {
      return [
        SubmissionStatus.TaApproved,
        SubmissionStatus.Completed,
        SubmissionStatus.CompletedButNoGrade,
      ].includes(activeSubmission.submissionStatus);
    }
    return true;
  }, [activeSubmission.submissionStatus, role]);

  const extraCredit = useMemo(() => activeSubmission.gradeOffset ?? 0, [activeSubmission]);
  const [extraCreditCustom, setExtraCreditCustom] = useState<number | null>(null);
  const extraComment = useMemo(() => activeSubmission.extraComment ?? '', [activeSubmission]);
  const [extraCommentCustom, setExtraCommentCustom] = useState<string | null>(null);
  useEffect(() => {
    setExtraCreditCustom(null);
    setExtraCommentCustom(null);
  }, [activeSubmission]);

  const assignmentRubrics = useMemo(
    () =>
      state.assignmentData?.assignment.assignmentRubrics.toSorted(
        (a, b) => a.priority - b.priority,
      ) ?? [],
    [state],
  );

  const [submissionCredits, setSubmissionCredits] = useState<
    Record<string, SubmissionRubricCredits | null>
  >({});
  useEffect(() => {
    const credits: Record<string, SubmissionRubricCredits> = {};
    activeSubmission.credits.forEach((credit) => {
      if (credit.rubricLevel) {
        credits[credit.rubricId] = {
          rubricId: credit.rubricId,
          comment: credit.comment,
          rubricLevelId: credit.rubricLevel.rubricLevelId,
        };
      }
    });

    // Auto-select "On-time Submission" rubric if submitted before due date
    const assignment = state.assignmentData?.assignment;
    if (!isReadOnly && assignment?.dueDate && activeSubmission.createAt) {
      if (isSubmissionOnTime(assignment.dueDate, activeSubmission.createAt)) {
        const onTimeRubric = assignmentRubrics.find(
          (ar) => ar.rubric?.rubricId === ON_TIME_SUBMISSION_RUBRIC_ID
        );

        if (onTimeRubric && !credits[ON_TIME_SUBMISSION_RUBRIC_ID]) {
          // Auto-select the highest grade level for on-time submission
          const highestGradeLevel = onTimeRubric.rubric?.rubricLevels.reduce(
            (max, level) => (level.grade > max.grade ? level : max),
            onTimeRubric.rubric.rubricLevels[0]
          );
          
          if (highestGradeLevel) {
            credits[ON_TIME_SUBMISSION_RUBRIC_ID] = {
              rubricId: ON_TIME_SUBMISSION_RUBRIC_ID,
              rubricLevelId: highestGradeLevel.rubricLevelId,
              comment: '',
            };
          }
        }
      }
    }

    setSubmissionCredits(credits);
  }, [activeSubmission]);

  const originalSubmissionCredits = useMemo<SubmissionCreditsInput>(() => {
    const credits: Record<string, SubmissionRubricCredits | null> = {};
    activeSubmission.credits.forEach((credit) => {
      if (credit.rubricLevel) {
        credits[credit.rubricId] = {
          rubricId: credit.rubricId,
          rubricLevelId: credit.rubricLevel.rubricLevelId,
          comment: credit.comment,
        };
      }
    });

    const submitData: SubmissionCreditsInput = {
      submissionId: activeSubmission.submissionId,
      extra: {
        extraComments: activeSubmission.extraComment ?? '',
        extraCredits: activeSubmission.gradeOffset ?? 0,
      },
      rubrics: Object.entries(credits).map(([rubricId, rubric]) => {
        return {
          rubricId,
          rubricLevelId: rubric?.rubricLevelId ?? null,
          comment: rubric?.comment ?? '',
        };
      }),
    };

    return submitData;
  }, [activeSubmission]);

  const pendingSubmissionCredits = useMemo<SubmissionCreditsInput>(() => {
    const submitData: SubmissionCreditsInput = {
      submissionId: activeSubmission.submissionId,
      extra: {
        extraComments: extraCommentCustom ?? activeSubmission.extraComment ?? '',
        extraCredits: extraCreditCustom ?? activeSubmission.gradeOffset ?? 0,
      },
      rubrics: isEmpty(submissionCredits)
        ? originalSubmissionCredits.rubrics
        : Object.entries(submissionCredits).map(([rubricId, rubric]) => {
            return {
              rubricId,
              rubricLevelId: rubric?.rubricLevelId ?? '',
              comment: rubric?.comment ?? '',
            };
          }),
    };

    return submitData;
  }, [
    activeSubmission.extraComment,
    activeSubmission.gradeOffset,
    activeSubmission.submissionId,
    extraCommentCustom,
    extraCreditCustom,
    originalSubmissionCredits.rubrics,
    submissionCredits,
  ]);

  const isPendingSave = useMemo(
    () => {
      const normalize = (input: SubmissionCreditsInput) => ({ ...input,
        rubrics: input.rubrics?.filter(r => r.rubricLevelId || r.comment).toSorted((a, b) => a.rubricId.localeCompare(b.rubricId)),
      });
      return !isEqual(normalize(pendingSubmissionCredits), normalize(originalSubmissionCredits));
    },
    [originalSubmissionCredits, pendingSubmissionCredits],
  );


  const [submitSubmissionCredits] = useMutation(SubmitAssignmentCreditsDocument);
  const [updateSubmissionStatus] = useMutation(UpdateSubmissionStatusDocument);

  useEffect(() => {
    if (role === 'ta') {
      if (
        [
          SubmissionStatus.TaApproved,
          SubmissionStatus.Completed,
          SubmissionStatus.CompletedButNoGrade,
        ].includes(activeSubmission.submissionStatus)
      ) {
        setSubmitDisabled(true);
        setSaveDisabled(true);
      } else {
        setSubmitDisabled(false);
        setSaveDisabled(!isPendingSave);
      }

      return;
    }

    if (role === 'teacher') {
      if (
        [SubmissionStatus.Completed, SubmissionStatus.CompletedButNoGrade].includes(
          activeSubmission.submissionStatus,
        )
      ) {
        setSubmitDisabled(true);
        setSaveDisabled(true);
      } else {
        setSubmitDisabled(false);
        setSaveDisabled(!isPendingSave);
      }

      return;
    }
  }, [activeSubmission, isPendingSave, role]);

  const saveEvaluation = async () => {
    const response = await submitSubmissionCredits({ variables: { input: pendingSubmissionCredits } });
    const saved = getFragmentData(TeacherEvaluationPageSubmissionFragmentDoc, response.data?.submitSubmissionCredits);
    if (!saved) throw new Error('服务器未返回保存结果');
    setActiveSubmission(saved);
  };
  const runAction = async (action: () => Promise<void>, pending: string, success: string) => {
    if (actionLock.current) return;
    actionLock.current = true;
    setActionBusy(true);
    try {
      await toast.promise(action(), { pending, success, error: { render: ({ data }) => (data as Error)?.message || '操作失败，请重试' } });
    } catch { /* Preserve the draft and unlock controls for retry. */ }
    finally { actionLock.current = false; setActionBusy(false); }
  };
  const changeStatus = async (submissionStatus: SubmissionStatus) => {
    const response = await updateSubmissionStatus({ variables: { submissionId: activeSubmission.submissionId, submissionStatus } });
    const saved = getFragmentData(TeacherEvaluationPageSubmissionFragmentDoc, response.data?.updateSubmissionStatus);
    if (!saved) throw new Error('服务器未返回提交结果');
    setActiveSubmission(saved);
  };
  const onSave = () => void runAction(saveEvaluation, '正在保存', '已保存');
  const onSubmit = () => void runAction(async () => {
    await changeStatus(role === 'ta' ? SubmissionStatus.TaApproved : SubmissionStatus.Completed);
    setShowSubmitConfirm(false);
  }, '正在提交', '已提交');
  const onRevert = () => void runAction(async () => {
    await changeStatus(SubmissionStatus.Submitted);
    setShowRevertConfirm(false);
  }, '正在撤回成绩', '已撤回成绩');
  const onRetract = () => void runAction(async () => {
    await changeStatus(SubmissionStatus.Retracted);
    setShowRetractConfirm(false);
  }, '正在打回', '已打回');

  const previewGrade = useMemo(() => {
    const grades = Object.entries(submissionCredits).map(([rubricId, rubric]) => {
      const assignmentRubric = assignmentRubrics.find((ar) => ar.rubric?.rubricId === rubricId);
      if (!assignmentRubric) {
        return 0;
      }
      const rubricLevel = assignmentRubric?.rubric?.rubricLevels.find(
        (rl) => rl.rubricLevelId === rubric?.rubricLevelId,
      );
      if (!rubricLevel) {
        return 0;
      }
      return rubricLevel.grade * assignmentRubric.weight;
    });

    return sum(grades) + (extraCreditCustom ?? extraCredit);
  }, [submissionCredits, extraCreditCustom, extraCredit, assignmentRubrics]);

  const requestAiEvaluation = () => void runAction(async () => {
    // Persist the current grading first so the queued snapshot includes the teacher's edits.
    if (isPendingSave && !isReadOnly) await saveEvaluation();
    await enqueueAi(aiJob?.status === 'failed');
    setShowAiEvaluation(true);
  }, '正在加入生成队列', '已加入生成队列，可切换作业或关闭页面');

  const handleRubricLevelChange = (rubricId: string) => (rubricLevelId: string) => {
    setSubmissionCredits((prev) => {
      return {
        ...prev,
        [rubricId]: { rubricId, rubricLevelId, comment: prev[rubricId]?.comment ?? '' },
      };
    });
  };

  const handleRubricCommentChange =
    (rubricId: string): React.ChangeEventHandler<HTMLInputElement> =>
    (e) => {
      setSubmissionCredits((prev) => {
        return {
          ...prev,
          [rubricId]: {
            rubricId,
            rubricLevelId: prev[rubricId]?.rubricLevelId ?? '',
            comment: e.target?.value ?? '',
          },
        };
      });
    };

  const toggleRubricLevel = (rubricId: string) => () => {
    if (isReadOnly || actionBusy) {
      return;
    }

    const rubricLevels = assignmentRubrics.find((ar) => ar.rubric?.rubricId === rubricId)?.rubric
      ?.rubricLevels;

    if (!rubricLevels) {
      return;
    }

    // Check if this is the on-time submission rubric and if it was auto-selected
    const assignment = state.assignmentData?.assignment;
    if (rubricId === ON_TIME_SUBMISSION_RUBRIC_ID && assignment?.dueDate && activeSubmission.createAt) {
      if (isSubmissionOnTime(assignment.dueDate, activeSubmission.createAt) && submissionCredits[rubricId]) {
        return; // Do not allow unchecking
      }
    }


    if (submissionCredits[rubricId]) {
      setSubmissionCredits((prev) => {
        return {
          ...prev,
          [rubricId]: null,
        };
      });
    } else {
      setSubmissionCredits((prev) => {
        return {
          ...prev,
          [rubricId]: { rubricLevelId: rubricLevels[0].rubricLevelId, rubricId, comment: '' },
        };
      });
    }
  };

  return (
    <div className="grade-comment-editor__container text-white">
      <div className="flex justify-between px-6 py-6">
        {assignmentRubrics
          .filter((ar) => ar.rubric.graderRoleType === 'TA')
          .map((r) => {
            return (
              <div
                className={cx(
                  'flex items-center justify-center text-center w-16 h-16 rounded-full text-black',
                  isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer',
                  submissionCredits[r.rubric.rubricId] ? 'bg-[#05ED1C]' : 'bg-white',
                )}
                key={`${JSON.stringify(r)}`}
                onClick={toggleRubricLevel(r.rubric.rubricId)}
              >
                {r.rubric.name}
              </div>
            );
          })}
      </div>
      {role === 'teacher' && (
        <div className="flex flex-col">
          <header className="text-[14px] leading-[20px] tracking-[0.1em] opacity-60 ">
            知识点
          </header>
          <div className="flex flex-col">
            <div className="flex flex-col border-y">
              {assignmentRubrics
                .filter((ar) => ar.rubric.graderRoleType === 'Teacher')
                .map((r) => {
                  return (
                    <RubricInput
                      key={`rubric-input-${r.rubric.rubricId}`}
                      handleRubricCommentChange={handleRubricCommentChange}
                      assignmentRubric={r}
                      isReadOnly={isReadOnly || actionBusy}
                      handleRubricLevelChange={handleRubricLevelChange}
                      rubricLevelValue={submissionCredits[r.rubric.rubricId]?.rubricLevelId ?? ''}
                      rubricComment={submissionCredits[r.rubric.rubricId]?.comment ?? ''}
                    ></RubricInput>
                  );
                })}
              <div className="text-[16px] leading-[50px] flex justify-between mr-4">
                <span>
                  <label htmlFor="extra-comment">特别加分</label>
                </span>
                <GradeNumberSelect
                  readOnly={isReadOnly || actionBusy}
                  value={extraCreditCustom ?? extraCredit}
                  onChange={setExtraCreditCustom}
                ></GradeNumberSelect>
              </div>
              <div className="border-x-0 border-y border-white border-dashed py-[10px] flex">
                <div className="flex-shrink-0">整体评价：</div>
                <textarea
                  id="extra-comment"
                  disabled={isReadOnly || actionBusy}
                  className="bg-transparent text-white text-[14px] leading-[20px] w-[100%] carret-white"
                  rows={2}
                  draggable={false}
                  value={extraCommentCustom ?? extraComment}
                  onChange={(e) => setExtraCommentCustom(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center mt-[16px]">
        <div className="text-4xl flex-1 text-right pr-5">~{Math.round(previewGrade)}</div>
        <div className="text-black text-[20px] flex space-x-2">
          {role === 'teacher' &&
            [SubmissionStatus.Completed, SubmissionStatus.CompletedButNoGrade].includes(activeSubmission.submissionStatus) && (
              <button
                type="button"
                disabled={actionBusy}
                className="flex items-center justify-center bg-[#f0403c] w-[60px] h-[60px] rounded-[50%] disabled:bg-[#c5c5c5]"
                onClick={() => setShowRevertConfirm(true)}
              >
                撤回
              </button>
            )}
          {role === 'ta' && (
            <button
              type="button"
              disabled={actionBusy || activeSubmission.submissionStatus !== SubmissionStatus.Submitted}
              className="flex items-center justify-center bg-[#f0403c] w-[60px] h-[60px] rounded-[50%] disabled:bg-[#c5c5c5]"
              onClick={() => setShowRetractConfirm(true)}
            >
              打回
            </button>
          )}
          <button
            type="button"
            disabled={saveDisabled || actionBusy}
            className="flex items-center justify-center bg-[#e8ed05] w-[60px] h-[60px] rounded-[50%] disabled:bg-[#c5c5c5]"
            onClick={onSave}
          >
            保存
          </button>
          <button
            type="button"
            disabled={submitDisabled || isPendingSave || actionBusy}
            className="flex items-center justify-center bg-[#05ED1C] w-[60px] h-[60px] rounded-[50%] disabled:bg-[#c5c5c5]"
            onClick={() => setShowSubmitConfirm(true)}
          >
            提交
          </button>
        </div>
      </div>
      {role === 'teacher' && (
        <section className="ai-evaluation-drawer mt-4 border-t border-white/25 pt-2">
          <button
            type="button"
            className="ai-evaluation-drawer__trigger flex w-full flex-col items-center justify-center gap-1 py-2 text-[12px] text-white/80 disabled:opacity-40"
            aria-expanded={showAiEvaluation}
            disabled={assignmentRubrics.length === 0}
            onClick={() => setShowAiEvaluation(value => !value)}
          >
            <span>AI 评价{aiJob?.status === 'queued' ? ' · 排队中' : aiJob?.status === 'running' ? ' · 正在生成' : aiJob?.status === 'completed' ? ' · 已保存' : ''}</span>
            <svg
              className={cx('transition-transform', showAiEvaluation && 'rotate-180')}
              width="18"
              height="10"
              viewBox="0 0 18 10"
              fill="none"
              aria-hidden="true"
            >
              <path d="M1 1L9 9L17 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {showAiEvaluation && (
            <div className="ai-evaluation-drawer__panel mt-2 rounded-xl bg-[#f3f4f6] p-3 text-[#202124] shadow-inner">
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className="text-[12px] font-semibold">三个模型的评价参考</span>
                {!aiLoading && (!aiJob || aiJob.status === 'failed') && (
                  <button
                    type="button"
                    className="text-[11px] text-blue-600 hover:underline"
                    disabled={actionBusy} onClick={requestAiEvaluation}
                  >
                    {aiJob?.status === 'failed' ? '重试生成' : '生成 AI 评价'}
                  </button>
                )}
              </div>
              {aiLoading && <div className="py-8 text-center text-[12px] text-black/55">{aiJob?.status === 'queued' ? '已加入队列，等待生成。可切换作业或关闭页面。' : aiJob?.status === 'running' ? '正在后台生成，可切换作业或关闭页面。' : '正在读取已保存的评价…'}</div>}
              {(aiError || aiJob?.error) && <div className="select-text rounded-lg bg-red-50 p-3 text-[12px] leading-5 text-red-700">{aiError || aiJob?.error}</div>}
              {!aiLoading && (
                <div className="flex flex-col gap-3">
                  {aiResults.map((result) => (
                    <article key={result.provider} className="select-text rounded-lg border border-black/10 bg-white p-3">
                      <header className="mb-2 flex items-start justify-between gap-2">
                        <div>
                          <h3 className="text-[13px] font-semibold">{aiProviderNames[result.provider]}</h3>
                          {result.model && <div className="mt-0.5 break-all text-[10px] text-black/40">{result.model}</div>}
                        </div>
                        <span className="whitespace-nowrap text-[10px] text-black/40">{result.durationMs} ms</span>
                      </header>
                      {result.evaluation ? (
                        <div className="ai-evaluation-markdown text-[12px] leading-5">
                          <ReactMarkdown>{result.evaluation}</ReactMarkdown>
                        </div>
                      ) : (
                        <div className="rounded-md bg-red-50 p-2 text-[11px] leading-5 text-red-700">{result.error}</div>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}
      {showRevertConfirm && currentIdentity?.identity === 'teacher' && (
        <div className="fullscreen-overlay z-[1000] flex justify-center items-center">
          <div className="z-[1001] relative flex flex-col items-center justify-center bg-white w-96">
            <div className="text-black text-5xl py-16">确认撤回成绩</div>
            <div className=" text-black text-3xl flex space-x-8 mt-auto mb-8 ">
              <button
                onClick={() => setShowRevertConfirm(false)}
                className=" flex items-center justify-center w-20 h-20 bg-[#e8ed05]  rounded-full"
                type="button"
              >
                返回
              </button>
              <button
                disabled={actionBusy} onClick={onRevert}
                className=" flex items-center justify-center w-20 h-20 bg-[#05ED1C]  rounded-full"
                type="button"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}
      {showSubmitConfirm && currentIdentity?.identity === 'teacher' && (
        <div className="fullscreen-overlay z-[1000] ">
          <div className="z-[1001] relative flex justify-center">
            <Transcript submissionId={activeSubmission.submissionId}></Transcript>
            <div className=" text-black text-3xl flex flex-col space-y-5 mt-auto mb-8 ml-16">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className=" flex items-center justify-center w-[100px] h-[100px] bg-[#e8ed05]  rounded-full"
                type="button"
              >
                返回
              </button>
              <button
                disabled={actionBusy} onClick={onSubmit}
                className=" flex items-center justify-center w-[100px] h-[100px] bg-[#05ED1C]  rounded-full"
                type="button"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}
      {showSubmitConfirm && currentIdentity?.identity === 'ta' && (
        <div className="fullscreen-overlay z-[1000] flex justify-center items-center">
          <div className="z-[1001] relative flex flex-col items-center justify-center bg-white w-96">
            <div className="text-black text-5xl py-16">确认提交</div>
            <div className=" text-black text-3xl flex space-x-8 mt-auto mb-8 ">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className=" flex items-center justify-center w-20 h-20 bg-[#e8ed05]  rounded-full"
                type="button"
              >
                返回
              </button>
              <button
                disabled={actionBusy} onClick={onSubmit}
                className=" flex items-center justify-center w-20 h-20 bg-[#05ED1C]  rounded-full"
                type="button"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}
      {showRetractConfirm && currentIdentity?.identity === 'ta' && (
        <div className="fullscreen-overlay z-[1000] flex justify-center items-center">
          <div className="z-[1001] relative flex flex-col items-center justify-center bg-white w-96">
            <div className="text-black text-5xl py-16">确认打回</div>
            <div className=" text-black text-3xl flex space-x-8 mt-auto mb-8 ">
              <button
                onClick={() => setShowRetractConfirm(false)}
                className=" flex items-center justify-center w-20 h-20 bg-[#e8ed05] rounded-full"
                type="button"
              >
                返回
              </button>
              <button
                disabled={actionBusy} onClick={onRetract}
                className=" flex items-center justify-center w-20 h-20 bg-[#05ED1C] rounded-full"
                type="button"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
